import React, { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  addDays,
  differenceInCalendarDays,
  format,
  isValid,
  parseISO,
  startOfDay,
} from 'date-fns';
import api from '../config/api';
import SEOHead from '../components/SEOHead';
import ItineraryStop from '../components/ItineraryStop';
import ItineraryReservations from '../components/ItineraryReservations';
import {
  DOCUMENT,
  PAGE_CONTAINER_MAX_WIDTH,
  documentPageContainerSx,
} from '../constants/documentTheme';
import {
  formatTime24,
  getDayTimeSpan,
  isReservationOnlyItem,
} from '../utils/itineraryDocumentUtils';
import { isItineraryItemOnDay } from '../utils/itineraryDaySpan';

const TripMapView = lazy(() => import('../components/TripMap/TripMapView'));

function parseTripDate(value) {
  if (!value) return null;
  try {
    const raw = String(value);
    const iso = raw.includes('T') ? raw : `${raw.slice(0, 10)}T12:00:00`;
    const d = parseISO(iso);
    return isValid(d) ? startOfDay(d) : null;
  } catch {
    return null;
  }
}

function buildItineraryByDays(trip, items) {
  const rows = (Array.isArray(items) ? items : []).filter((item) => !isReservationOnlyItem(item));
  const start = parseTripDate(trip?.start_date);
  const end = parseTripDate(trip?.end_date) || start;
  if (!start) {
    if (!rows.length) return [];
    return [{
      dayNumber: 1,
      date: parseTripDate(rows[0].start_datetime) || new Date(),
      items: rows,
    }];
  }

  const span = Math.max(0, differenceInCalendarDays(end || start, start));
  const days = [];
  for (let i = 0; i <= span && i < 45; i += 1) {
    const date = addDays(start, i);
    const dayItems = rows.filter((item) => isItineraryItemOnDay(item, date));
    days.push({ dayNumber: i + 1, date, items: dayItems });
  }
  return days;
}

const PublicTripView = () => {
  const { token } = useParams();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);
  const [expandedStopIds, setExpandedStopIds] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.get(`/public/trips/${encodeURIComponent(token)}`);
        if (!cancelled) setPayload(res.data);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || t('tripShare.publicLoadFailed'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, t]);

  const trip = payload?.trip;
  const itinerary = useMemo(
    () => (Array.isArray(payload?.itinerary) ? payload.itinerary : []),
    [payload?.itinerary]
  );
  const days = useMemo(() => buildItineraryByDays(trip, itinerary), [trip, itinerary]);
  const hasDayItems = days.some((day) => day.items.length > 0);
  const hasReservations = itinerary.some((item) => isReservationOnlyItem(item));

  const dateLabel = useMemo(() => {
    if (!trip?.start_date) return '';
    try {
      const start = format(parseTripDate(trip.start_date), 'MMM d, yyyy');
      if (!trip.end_date) return start;
      const end = format(parseTripDate(trip.end_date), 'MMM d, yyyy');
      return start === end ? start : `${start} – ${end}`;
    } catch {
      return '';
    }
  }, [trip]);

  if (loading) {
    return (
      <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ ...documentPageContainerSx, py: 8, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !trip) {
    return (
      <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ ...documentPageContainerSx, py: 6 }}>
        <SEOHead
          title={t('tripShare.publicNotFoundTitle')}
          description={t('tripShare.publicNotFoundDesc')}
          noIndex
        />
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || t('tripShare.publicNotFoundDesc')}
        </Alert>
        <Button component={RouterLink} to="/register" variant="contained">
          {t('tripShare.joinCta')}
        </Button>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: DOCUMENT.canvas, minHeight: '70vh' }}>
      <SEOHead
        title={trip.title}
        description={trip.description || t('tripShare.publicDesc', { title: trip.title })}
        noIndex
      />
      <Box
        sx={{
          bgcolor: DOCUMENT.oxblood,
          color: DOCUMENT.ivory,
          py: { xs: 4, md: 6 },
          mb: 4,
        }}
      >
        <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={documentPageContainerSx}>
          <Typography
            variant="overline"
            sx={{ letterSpacing: '0.12em', opacity: 0.85, display: 'block', mb: 1 }}
          >
            {t('tripShare.readOnlyBadge')}
          </Typography>
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontFamily: DOCUMENT.display,
              fontWeight: 500,
              mb: 1,
              fontSize: { xs: '1.75rem', md: '2.5rem' },
            }}
          >
            {trip.title}
          </Typography>
          {(trip.destination || trip.first_location) && (
            <Typography variant="h6" sx={{ fontWeight: 400, opacity: 0.9, mb: 0.5 }}>
              {trip.destination || trip.first_location}
            </Typography>
          )}
          {dateLabel ? (
            <Typography variant="body1" sx={{ opacity: 0.85 }}>
              {dateLabel}
            </Typography>
          ) : null}
        </Container>
      </Box>

      <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ ...documentPageContainerSx, pb: 8 }}>
        {trip.description ? (
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 720 }}>
            {trip.description}
          </Typography>
        ) : null}

        <Box sx={{ mb: 4 }} data-testid="public-trip-map">
          <Suspense
            fallback={(
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            )}
          >
            <TripMapView
              tripId={trip.id}
              source="share"
              token={token}
              showList
            />
          </Suspense>
        </Box>

        {hasReservations ? (
          <Box sx={{ mb: 4 }}>
            <ItineraryReservations
              items={itinerary}
              flightStatusEnabled={false}
              canEditItem={() => false}
            />
          </Box>
        ) : null}

        {!hasDayItems && !hasReservations ? (
          <Alert severity="info">{t('tripShare.emptyItinerary')}</Alert>
        ) : (
          days.map((day) => {
            if (!day.items.length) return null;
            const span = getDayTimeSpan(day.items, formatTime24);
            return (
              <Box key={format(day.date, 'yyyy-MM-dd')} sx={{ mb: 4 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontFamily: DOCUMENT.display,
                    color: DOCUMENT.ink,
                    mb: 0.5,
                  }}
                >
                  {t('tripShare.dayLabel', {
                    number: day.dayNumber,
                    date: format(day.date, 'EEE, MMM d'),
                  })}
                </Typography>
                {span ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {span}
                  </Typography>
                ) : (
                  <Box sx={{ mb: 2 }} />
                )}
                {day.items.map((item, index) => {
                  const stopKey = item.public_id || String(item.id);
                  return (
                    <ItineraryStop
                      key={stopKey}
                      item={item}
                      isLast={index === day.items.length - 1}
                      nextItem={day.items[index + 1] || null}
                      currentDayDate={day.date}
                      expanded={Boolean(expandedStopIds[stopKey])}
                      onToggle={() => setExpandedStopIds((prev) => ({
                        ...prev,
                        [stopKey]: !prev[stopKey],
                      }))}
                      canEdit={false}
                      showGroupPlanTag={item.item_source === 'member_group'}
                    />
                  );
                })}
              </Box>
            );
          })
        )}

        <Box
          sx={{
            mt: 6,
            p: 3,
            border: `1px solid ${DOCUMENT.rule}`,
            bgcolor: DOCUMENT.ivory,
          }}
        >
          <Typography variant="h6" sx={{ fontFamily: DOCUMENT.display, mb: 1 }}>
            {t('tripShare.joinTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('tripShare.joinBody')}
          </Typography>
          <Button
            component={RouterLink}
            to={`/register?trip=${trip.id}`}
            variant="contained"
            sx={{
              bgcolor: DOCUMENT.oxblood,
              '&:hover': { bgcolor: DOCUMENT.nav },
            }}
          >
            {t('tripShare.joinCta')}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default PublicTripView;
